import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth";
import { MemberAuthProvider } from "@/lib/memberAuth";
import MarketingLayout from "@/components/MarketingLayout";
import AdminLayout from "@/components/AdminLayout";
import MemberLayout from "@/components/MemberLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Apply from "@/pages/Apply";
import Network from "@/pages/Network";
import PublicMeetings from "@/pages/PublicMeetings";

import AdminLogin from "@/pages/admin/AdminLogin";
import Overview from "@/pages/admin/Overview";
import Leads from "@/pages/admin/Leads";
import Research from "@/pages/admin/Research";
import Outreach from "@/pages/admin/Outreach";
import Replies from "@/pages/admin/Replies";
import Applications from "@/pages/admin/Applications";
import Members from "@/pages/admin/Members";
import Content from "@/pages/admin/Content";
import ChatSessions from "@/pages/admin/ChatSessions";
import Activity from "@/pages/admin/Activity";
import Agents from "@/pages/admin/Agents";
import Autopilot from "@/pages/admin/Autopilot";
import Integrations from "@/pages/admin/Integrations";
import Budget from "@/pages/admin/Budget";
import Meetings from "@/pages/admin/Meetings";
import Settings from "@/pages/admin/Settings";
import LinkedIn from "@/pages/admin/LinkedIn";
import Playbook from "@/pages/admin/Playbook";

import MemberLogin from "@/pages/portal/MemberLogin";
import MemberForgotPassword from "@/pages/portal/MemberForgotPassword";
import MemberSetPassword from "@/pages/portal/MemberSetPassword";
import MemberDashboard from "@/pages/portal/MemberDashboard";
import MemberDirectory from "@/pages/portal/MemberDirectory";
import MemberEvents from "@/pages/portal/MemberEvents";
import MemberMatches from "@/pages/portal/MemberMatches";
import MemberProfile from "@/pages/portal/MemberProfile";
import MemberShipments from "@/pages/portal/MemberShipments";
import MemberPayments from "@/pages/portal/MemberPayments";
import MemberDownloads from "@/pages/portal/MemberDownloads";
import MemberHolidays from "@/pages/portal/MemberHolidays";
import MemberNews from "@/pages/portal/MemberNews";
import MemberVendors from "@/pages/portal/MemberVendors";
import Templates from "@/pages/admin/Templates";
import Founding from "@/pages/admin/Founding";
import { VendorApply, VendorSuccess, VendorCancel } from "@/pages/VendorApply";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <MemberAuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <Routes>
              {/* Public */}
              <Route element={<MarketingLayout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/apply" element={<Apply />} />
                <Route path="/network" element={<Network />} />
                <Route path="/meetings" element={<PublicMeetings />} />
                {/* Back-compat: old /members URL redirects to /network */}
                <Route path="/members" element={<Network />} />
                <Route path="/vendors/apply" element={<VendorApply />} />
              </Route>

              {/* Vendor Stripe result pages (no layout — standalone) */}
              <Route path="/vendors/success" element={<VendorSuccess />} />
              <Route path="/vendors/cancel" element={<VendorCancel />} />

              {/* Back-compat redirects for the old member login route */}
              <Route path="/member-login" element={<Navigate to="/portal/login" replace />} />

              {/* Member portal login / password screens (no MemberLayout wrapper) */}
              <Route path="/portal/login" element={<MemberLogin />} />
              <Route path="/portal/forgot-password" element={<MemberForgotPassword />} />
              <Route path="/portal/set-password" element={<MemberSetPassword />} />

              {/* Member portal protected */}
              <Route element={<MemberLayout />}>
                <Route path="/portal" element={<MemberDashboard />} />
                <Route path="/portal/directory" element={<MemberDirectory />} />
                <Route path="/portal/events" element={<MemberEvents />} />
                <Route path="/portal/matches" element={<MemberMatches />} />
                <Route path="/portal/shipments" element={<MemberShipments />} />
                <Route path="/portal/payments" element={<MemberPayments />} />
                <Route path="/portal/downloads" element={<MemberDownloads />} />
                <Route path="/portal/holidays" element={<MemberHolidays />} />
                <Route path="/portal/news" element={<MemberNews />} />
                <Route path="/portal/vendors" element={<MemberVendors />} />
                <Route path="/portal/profile" element={<MemberProfile />} />
              </Route>

              {/* Admin login */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Admin protected */}
              <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route path="/admin" element={<Overview />} />
                <Route path="/admin/leads" element={<Leads />} />
                <Route path="/admin/research" element={<Research />} />
                <Route path="/admin/outreach" element={<Outreach />} />
                <Route path="/admin/replies" element={<Replies />} />
                <Route path="/admin/applications" element={<Applications />} />
                <Route path="/admin/members" element={<Members />} />
                <Route path="/admin/content" element={<Content />} />
                <Route path="/admin/chat" element={<ChatSessions />} />
                <Route path="/admin/activity" element={<Activity />} />
                <Route path="/admin/agents" element={<Agents />} />
                <Route path="/admin/autopilot" element={<Autopilot />} />
                <Route path="/admin/integrations" element={<Integrations />} />
                <Route path="/admin/budget" element={<Budget />} />
                <Route path="/admin/meetings" element={<Meetings />} />
                <Route path="/admin/linkedin" element={<LinkedIn />} />
                <Route path="/admin/playbook" element={<Playbook />} />
                <Route path="/admin/templates" element={<Templates />} />
                <Route path="/admin/founding" element={<Founding />} />
                <Route path="/admin/settings" element={<Settings />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </MemberAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
